import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  FileUp,
  Loader2,
  X,
} from 'lucide-react'
import LegalTemplates from '@services/legalTemplates'
import TemplateCanvas from './TemplateCanvas'

const MAX_PDF_BYTES = 20 * 1024 * 1024
const MAX_DOCX_BYTES = 10 * 1024 * 1024

export default function TemplateImportDialog({ areas, onClose, onImport }) {
  const dialog = useRef(null)
  const input = useRef(null)
  const task = useRef(null)
  const pdfCache = useRef(null)
  const [file, setFile] = useState(null)
  const [name, setName] = useState('')
  const [area, setArea] = useState(areas[0]?.key || 'civel')
  const [mode, setMode] = useState('appearance')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(0)

  useEffect(() => {
    const element = dialog.current
    element.showModal()
    return () => {
      task.current?.abort()
      pdfCache.current = null
      element.close()
    }
  }, [])

  const close = () => {
    task.current?.abort()
    pdfCache.current = null
    onClose()
  }
  const choose = (next) => {
    if (!next || busy) return
    setError('')
    setResult(null)
    pdfCache.current = null
    setFile(null)
    const extension = next.name.split('.').at(-1)?.toLowerCase()
    if (!['pdf', 'docx'].includes(extension))
      return setError(
        'Selecione um arquivo PDF ou DOCX. Arquivos .doc e .docm não são aceitos.',
      )
    const maximum = extension === 'docx' ? MAX_DOCX_BYTES : MAX_PDF_BYTES
    if (!next.size || next.size > maximum)
      return setError(
        `O arquivo deve ter até ${maximum / 1024 / 1024} MB e conter um documento válido.`,
      )
    setFile(next)
    setName(next.name.replace(/\.(pdf|docx)$/i, '').slice(0, 160))
    setMode(extension === 'docx' ? 'editable' : 'appearance')
    setPage(0)
  }

  const convert = async () => {
    if (!file || busy) return
    const controller = new AbortController()
    task.current = controller
    setBusy(true)
    setError('')
    setResult(null)
    setPage(0)
    const docx = file.name.toLowerCase().endsWith('.docx')
    try {
      let cached = pdfCache.current
      if (!cached) {
        if (docx) {
          setProgress({ label: 'Preparando as páginas do Word…', percent: 5 })
          const response = await LegalTemplates.convertDocx(
            file,
            controller.signal,
          )
          if (controller.signal.aborted) return
          if (!response.success) throw new Error(response.error)
          cached = {
            bytes: await response.data.arrayBuffer(),
            warnings: response.warnings || [],
          }
        } else cached = { bytes: await file.arrayBuffer(), warnings: [] }
        if (controller.signal.aborted) return
        pdfCache.current = cached
      }
      if (controller.signal.aborted) return
      setProgress({ label: 'Lendo o documento…', percent: 10 })
      const { importPdf } = await import('../import/pdfImport.js')
      const imported = await importPdf(cached.bytes, {
        mode,
        signal: controller.signal,
        onProgress: ({ page: current, total, percent }) => {
          if (!controller.signal.aborted)
            setProgress({
              label: total
                ? `Preparando página ${current || 1} de ${total}…`
                : 'Lendo o documento…',
              percent: 10 + Math.min(100, percent || 0) * 0.9,
            })
        },
      })
      if (controller.signal.aborted) return
      setResult({
        ...imported,
        warnings: [...new Set([...cached.warnings, ...imported.warnings])],
      })
    } catch (exception) {
      if (!controller.signal.aborted)
        setError(exception.message || 'Não foi possível importar este arquivo.')
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false)
        setProgress(null)
      }
      if (task.current === controller) task.current = null
    }
  }

  const accept = () => {
    if (!result || !name.trim())
      return setError('Dê um nome ao modelo antes de abrir no editor.')
    onImport({
      name: name.trim(),
      area,
      description: '',
      document: result.document,
    })
  }
  const changeMode = (value) => {
    setMode(value)
    setResult(null)
    setPage(0)
  }

  return createPortal(
    <dialog
      ref={dialog}
      className="lt-library lt-import-dialog"
      aria-labelledby="lt-import-title"
      onCancel={(event) => {
        event.preventDefault()
        close()
      }}
    >
      <header className="lt-import-header">
        <div>
          <span className="lt-eyebrow">PADRÕES DO ESCRITÓRIO</span>
          <h2 id="lt-import-title">Importar modelo</h2>
          <p>O modelo só entra na biblioteca quando você clicar em Salvar.</p>
        </div>
        <button
          className="lt-icon-button"
          aria-label="Fechar importação"
          onClick={close}
        >
          <X size={20} />
        </button>
      </header>
      <div className="lt-import-body">
        <div className="lt-import-settings">
          <button
            className="lt-import-upload"
            disabled={busy}
            onClick={() => input.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault()
              choose(event.dataTransfer.files[0])
            }}
          >
            <FileUp size={28} />
            <strong>{file ? file.name : 'Escolher DOCX ou PDF'}</strong>
            <span>
              {file
                ? `${(file.size / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} MB · Clique para trocar`
                : 'Arraste aqui ou clique para selecionar'}
            </span>
            <small>DOCX até 10 MB · PDF até 20 MB · até 30 páginas</small>
          </button>
          <input
            ref={input}
            type="file"
            accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            aria-label="Arquivo do modelo"
            hidden
            onChange={(event) => {
              choose(event.target.files?.[0])
              event.target.value = ''
            }}
          />
          <label className="lt-field">
            <span>Nome do modelo</span>
            <input
              value={name}
              maxLength={160}
              onChange={(event) => setName(event.target.value)}
              disabled={busy}
              placeholder="Ex.: Petição inicial — padrão do escritório"
            />
          </label>
          <label className="lt-field">
            <span>Área jurídica do modelo</span>
            <select
              value={area}
              onChange={(event) => setArea(event.target.value)}
              disabled={busy}
            >
              {areas.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="lt-import-modes" disabled={busy}>
            <legend>Como importar</legend>
            <label>
              <input
                type="radio"
                name="lt-import-mode"
                value="appearance"
                checked={mode === 'appearance'}
                onChange={() => changeMode('appearance')}
              />
              <span>
                <strong>Preservar aparência</strong>
                <small>
                  As páginas viram imagens de fundo. Textos originais ficam na
                  imagem; você pode adicionar novos elementos por cima.
                </small>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="lt-import-mode"
                value="editable"
                checked={mode === 'editable'}
                onChange={() => changeMode('editable')}
              />
              <span>
                <strong>Extrair texto editável</strong>
                <small>
                  Recria os textos em caixas editáveis. Fontes e espaçamentos
                  podem mudar; páginas escaneadas permanecem como imagens.
                </small>
              </span>
            </label>
          </fieldset>
          {file?.name.toLowerCase().endsWith('.docx') && (
            <p className="lt-hint">
              No Word, fontes e recursos de diagramação podem variar na
              conversão. Confira a prévia antes de usar o modelo.
            </p>
          )}
          <button
            className="lt-button lt-primary lt-full"
            onClick={convert}
            disabled={!file || busy}
          >
            {busy ? (
              <Loader2 size={16} className="lt-spin" />
            ) : (
              <FileUp size={16} />
            )}
            {busy
              ? 'Importando…'
              : result
                ? 'Gerar prévia novamente'
                : 'Pré-visualizar importação'}
          </button>
          {progress && (
            <div className="lt-import-progress" role="status">
              <span>{progress.label}</span>
              <progress max="100" value={progress.percent} />
            </div>
          )}
          {error && (
            <p className="lt-import-error" role="alert">
              <AlertCircle size={17} />
              <span>{error}</span>
            </p>
          )}
        </div>
        <div className="lt-import-result">
          {result ? (
            <>
              <div className="lt-import-page-nav">
                <button
                  className="lt-icon-button"
                  disabled={page === 0}
                  aria-label="Página anterior da importação"
                  onClick={() => setPage((current) => current - 1)}
                >
                  <ArrowLeft size={16} />
                </button>
                <span>
                  Página {page + 1} de {result.document.pages.length}
                </span>
                <button
                  className="lt-icon-button"
                  disabled={page === result.document.pages.length - 1}
                  aria-label="Próxima página da importação"
                  onClick={() => setPage((current) => current + 1)}
                >
                  <ArrowRight size={16} />
                </button>
              </div>
              <div className="lt-import-paper">
                <TemplateCanvas
                  document={{
                    ...result.document,
                    pages: [result.document.pages[page]],
                  }}
                  zoom={0.42}
                  preview
                  showGuides={false}
                />
              </div>
              <p className="lt-import-summary">
                {result.document.pages.length} página(s) ·{' '}
                {result.editableTextCount} caixa(s) de texto editável
              </p>
              {result.warnings.length > 0 && (
                <div className="lt-import-notices" role="status">
                  <strong>Confira estes pontos</strong>
                  <ul>
                    {result.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="lt-import-placeholder">
              <FileUp size={40} strokeWidth={1.2} />
              <h3>Seu padrão, dentro do editor</h3>
              <p>
                A prévia aparece aqui. Depois, ajuste textos, inclua campos e
                salve na biblioteca.
              </p>
            </div>
          )}
        </div>
      </div>
      <footer className="lt-import-footer">
        <button className="lt-button" onClick={close}>
          {busy ? 'Cancelar importação' : 'Cancelar'}
        </button>
        <button
          className="lt-button lt-primary"
          disabled={!result || busy || !name.trim()}
          onClick={accept}
        >
          Abrir no editor <ArrowRight size={16} />
        </button>
      </footer>
    </dialog>,
    document.body,
  )
}
